import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, Form, FormSubmitHandler} from 'platform/components';
import {DeepPartial} from 'utility-types';

import {useDispatch} from 'react-redux';
import {useLocation, useParams} from 'react-router-dom';

import {defaultTo, isNil, isNotNil, mergeAll} from 'ramda';
import {isBoolean, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {documentContextApi, useGetSeriesListQuery} from '@dms/api/core';
import {VatRateType} from '@dms/api/shared';
import {
  useGetServiceOrderTypesQuery,
  useGetServiceOrderVariantAccountingQuery,
  useGetServiceOrderVariantDefaultWarehousePriorityQuery,
  useGetServiceOrderVariantDepositQuery,
  useGetServiceOrderVariantDocumentsQuery,
  useGetServiceOrderVariantIntegrationQuery,
  useGetServiceOrderVariantMandatoryFieldsQuery,
  useGetServiceOrderVariantPaymentTypeQuery,
  useGetServiceOrderVariantQuery,
  useGetServiceOrderVariantReportingQuery,
  useGetServiceOrderVariantWarehousePriorityQuery,
  usePatchServiceOrderVariantAccountingMutation,
  usePatchServiceOrderVariantDepositMutation,
  usePatchServiceOrderVariantDocumentsMutation,
  usePatchServiceOrderVariantIntegrationMutation,
  usePatchServiceOrderVariantMandatoryFieldsMutation,
  usePatchServiceOrderVariantMutation,
  usePatchServiceOrderVariantPaymentTypeMutation,
  usePatchServiceOrderVariantReportingMutation,
  usePatchServiceOrderVariantWarehousePriorityMutation,
  usePostServiceOrderVariantMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {
  buildDepositDefaultValues,
  decimalValueToPercentage,
  percentageToDecimalValue,
  SettingsTemplate,
  resolveVariantTemplateId,
} from '@dms/settings/shared';
import {handleApiError, queryParams, useDocumentTemplates} from '@dms/shared';

import {Nullish, stringToBoolean, useNavigate, useQueryState} from 'shared/web';

import {AccountingTab} from './(sections)/AccountingTab/AccountingTab';
import {DepositTab} from './(sections)/DepositTab/DepositTab';
import {DocumentsTab} from './(sections)/DocumentsTab/DocumentsTab';
import {GeneralTab} from './(sections)/GeneralTab/GeneralTab';
import {IntegrationTab} from './(sections)/IntegrationTab/IntegrationTab';
import {MandatoryFieldsTab} from './(sections)/MandatoryFieldsTab/MandatoryFieldsTab';
import {PaymentTab} from './(sections)/PaymentTab/PaymentTab';
import {ReportingTab} from './(sections)/ReportingTab/ReportingTab';
import {WarehousePrioritiesTab} from './(sections)/WarehousePrioritiesTab/WarehousePrioritiesTab';
import {DOCUMENT_SERIES_TYPES} from './constants/documentSeriesTypes';
import {VEHICLE_MANDATORY_FIELDS} from './constants/vehicleMandatoryFields';
import {FormType, MandatoryFieldType} from './types/ServiceOrderVariant';
import {ServiceOrderVariantsDetailFormSchema} from './types/ServiceOrderVariantsDetailFormSchema';
import {buildDepositPatchBody} from './utils/buildDepositPatchBody';
import {buildDocumentsDefaultValues} from './utils/buildDocumentsDefaultValues';
import {buildPaymentTypeDefaultValues} from './utils/buildPaymentTypeDefaultValues';
import {buildPaymentTypePatchBody} from './utils/buildPaymentTypePatchBody';
import {getFieldsByOrderType} from './utils/getFieldsByOrderType';
import {getPrimaryDefaultTemplateId} from './utils/getPrimaryDefaultTemplateId';
import {getServiceOrderVariantsRoutes} from './utils/getServiceOrderVariantsRoutes';
import {mandatoryConditions} from './utils/mandatoryConditions';

const GENERAL_TAB = 'general';
const DOCUMENTS_TAB = 'documents';
const PAYMENTS_TAB = 'payments';
const DEPOSIT_TAB = 'deposit';
const MANDATORY_FIELDS_TAB = 'mandatoryFields';
const INTEGRATION_TAB = 'integration';
const ACCOUNTING_TAB = 'accounting';
const REPORTING_TAB = 'reporting';
const WAREHOUSES_TAB = 'warehouses';

// In tab order, so a blocked save lands on the first tab that actually renders the offending field.
const TABS_BY_FORM_SECTION: [keyof FormType, string][] = [
  ['general', GENERAL_TAB],
  ['documents', DOCUMENTS_TAB],
  ['paymentType', PAYMENTS_TAB],
  ['deposit', DEPOSIT_TAB],
  ['accounting', ACCOUNTING_TAB],
  ['reporting', REPORTING_TAB],
  ['mandatoryFields', MANDATORY_FIELDS_TAB],
  ['mandatoryFieldConditions', MANDATORY_FIELDS_TAB],
  ['warehousePriorities', WAREHOUSES_TAB],
  ['integration', INTEGRATION_TAB],
];

const DEFAULT_VAT_RATE: VatRateType = 'S';

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function ServiceOrderVariantsDetail() {
  const {id} = useParams();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [, updateQuery] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const serviceOrderVariantsRoutes = getServiceOrderVariantsRoutes(pathname);

  const isSalesRoute = pathname.startsWith(settingsRoutes.salesServiceOrderVariants);

  const [getTemplates, {isLoading: isTemplatesLoading}] = useDocumentTemplates();
  const standardInvoice = getTemplates('workshop-invoice');
  const internalInvoice = getTemplates('workshop-invoice_internal');

  // `resolveVariantTemplateId` only repoints an existing id, and counts a stored empty string as
  // one, so the primary is applied on emptiness rather than with `??`.
  const standardPrimaryTemplateId = getPrimaryDefaultTemplateId(standardInvoice.defaultTemplates);
  const internalPrimaryTemplateId = getPrimaryDefaultTemplateId(internalInvoice.defaultTemplates);

  const resolveStandardInvoiceTemplateId = (templateId: string | Nullish) => {
    const resolvedTemplateId = resolveVariantTemplateId(
      standardInvoice.definition?.templates,
      standardPrimaryTemplateId,
      templateId
    );

    return isNotNilOrEmpty(resolvedTemplateId) ? resolvedTemplateId : standardPrimaryTemplateId;
  };

  const resolveInternalInvoiceTemplateId = (templateId: string | Nullish) => {
    const resolvedTemplateId = resolveVariantTemplateId(
      internalInvoice.definition?.templates,
      internalPrimaryTemplateId,
      templateId
    );

    return isNotNilOrEmpty(resolvedTemplateId) ? resolvedTemplateId : internalPrimaryTemplateId;
  };

  const {
    data: orderVariant,
    isLoading: isOrderVariantLoading,
    isError: isOrderVariantError,
  } = useGetServiceOrderVariantQuery(isNotNil(id) ? {serviceOrderVariantId: id} : skipToken);
  const {
    data: orderVariantDocuments,
    isLoading: isOrderVariantDocumentsLoading,
    isError: isOrderVariantDocumentsError,
  } = useGetServiceOrderVariantDocumentsQuery(
    isNotNil(id) ? {serviceOrderVariantId: id} : skipToken
  );
  const {
    data: mandatoryFields,
    isLoading: isMandatoryFieldsLoading,
    isError: isMandatoryFieldsError,
  } = useGetServiceOrderVariantMandatoryFieldsQuery(
    isNotNil(id) ? {serviceOrderVariantId: id} : skipToken
  );
  const {
    data: orderVariantPaymentType,
    isLoading: isOrderVariantPaymentTypeLoading,
    isError: isOrderVariantPaymentTypeError,
  } = useGetServiceOrderVariantPaymentTypeQuery(
    isNotNil(id) ? {serviceOrderVariantId: id} : skipToken
  );
  const {
    data: orderVariantDeposit,
    isLoading: isOrderVariantDepositLoading,
    isError: isOrderVariantDepositError,
  } = useGetServiceOrderVariantDepositQuery(isNotNil(id) ? {serviceOrderVariantId: id} : skipToken);
  const {
    data: orderVariantAccounting,
    isFetching: isOrderVariantAccountingFetching,
    isError: isOrderVariantAccountingError,
  } = useGetServiceOrderVariantAccountingQuery(
    isNotNil(id) ? {serviceOrderVariantId: id} : skipToken
  );
  const {
    data: orderVariantReporting,
    isFetching: isOrderVariantReportingFetching,
    isError: isOrderVariantReportingError,
  } = useGetServiceOrderVariantReportingQuery(
    isNotNil(id) ? {serviceOrderVariantId: id} : skipToken
  );
  const {
    data: orderVariantIntegration,
    isFetching: isOrderVariantIntegrationFetching,
    isError: isOrderVariantIntegrationError,
  } = useGetServiceOrderVariantIntegrationQuery(
    isNotNil(id) ? {serviceOrderVariantId: id} : skipToken
  );
  const {
    data: defaultWarehousePriority,
    isFetching: isDefaultWarehousePriorityFetching,
    isError: isDefaultWarehousePriorityError,
  } = useGetServiceOrderVariantDefaultWarehousePriorityQuery({}, {skip: !isNilOrEmpty(id)});
  const {
    data: warehousePriorities,
    isLoading: isWarehousePrioritiesLoading,
    isError: isWarehousePrioritiesError,
  } = useGetServiceOrderVariantWarehousePriorityQuery(
    isNotNil(id) ? {serviceOrderVariantId: id} : skipToken
  );
  const {data: orderTypes} = useGetServiceOrderTypesQuery();
  // Read here, not in a tab, because the series prefill belongs to the form's default values.
  const {
    data: series,
    isLoading: isSeriesLoading,
    isError: isSeriesError,
  } = useGetSeriesListQuery({
    type: DOCUMENT_SERIES_TYPES,
  });

  const [postOrderVariant] = usePostServiceOrderVariantMutation();
  const [patchOrderVariant] = usePatchServiceOrderVariantMutation();
  const [patchOrderVariantDocuments] = usePatchServiceOrderVariantDocumentsMutation();
  const [patchServiceOrderVariantMandatoryFields] =
    usePatchServiceOrderVariantMandatoryFieldsMutation();
  const [patchServiceOrderVariantAccounting] = usePatchServiceOrderVariantAccountingMutation();
  const [patchServiceOrderVariantReporting] = usePatchServiceOrderVariantReportingMutation();
  const [patchPaymentType] = usePatchServiceOrderVariantPaymentTypeMutation();
  const [patchDeposit] = usePatchServiceOrderVariantDepositMutation();
  const [patchServiceOrderVariantIntegration] = usePatchServiceOrderVariantIntegrationMutation();
  const [patchServiceOrderVariantWarehousePriority] =
    usePatchServiceOrderVariantWarehousePriorityMutation();

  const handleSubmit: FormSubmitHandler<FormType> = async (data) => {
    const body = {
      ...data.general,
      isActive: orderVariant?.isActive ?? true,
      serviceCaseTypeId: data.general?.serviceCaseTypeId ?? '',
      isDefaultForServiceCaseType: !!data.general?.isDefaultForServiceCaseType,
      serviceOrderTypeId: data.general?.serviceOrderTypeId ?? '',
      name: data.general?.name ?? '',
      priceType: data.general?.priceType ?? '',
      priceTypeRatio: percentageToDecimalValue(data.general?.priceTypeRatio ?? 100),
      rateType: data.general?.rateType ?? '',
      rateTypeRatio: percentageToDecimalValue(data.general?.rateTypeRatio ?? 100),
      minimalMaterialMargin: percentageToDecimalValue(data.general?.minimalMaterialMargin ?? 0),
      minimalWorkMargin: percentageToDecimalValue(data.general?.minimalWorkMargin ?? 0),
      suffix: data.general?.suffix ?? '',
      isUnitPriceWithVat: data.general?.isUnitPriceWithVat ?? false,
      isChangeableDefaultCustomer: data.general?.isChangeableDefaultCustomer ?? false,
      isCancelReasonRequired: data.general?.isCancelReasonRequired ?? false,
    };

    const orderCode = orderTypes?.find((type) => type?.id === body.serviceOrderTypeId)?.code;

    const mandatoryFields = [
      ...getFieldsByOrderType(orderCode),
      ...VEHICLE_MANDATORY_FIELDS.map((field) => field.id),
    ].map((mandatoryField) => ({
      field: mandatoryField,
      isSelected: !!data.mandatoryFields?.[mandatoryField],
      mandatoryCondition:
        mandatoryField === 'generatingOrderSheet'
          ? undefined
          : mandatoryConditions.map((condition) => ({
              event: condition,
              isSelected:
                !!data.mandatoryFields?.[mandatoryField] &&
                !!data.mandatoryFieldConditions?.[mandatoryField]?.includes(condition),
            })),
    }));

    await (
      isNil(id) ? postOrderVariant({body}) : patchOrderVariant({serviceOrderVariantId: id, body})
    )
      .unwrap()
      .then((response) => {
        const tabsArray = [];
        const serviceOrderVariantId = response?.serviceOrderVariantId ?? id ?? '';

        if (data.documents) {
          tabsArray.push(
            patchOrderVariantDocuments({
              serviceOrderVariantId,
              body: data.documents,
            })
              .unwrap()
              .catch(handleApiError)
          );
        }

        if (data.paymentType) {
          tabsArray.push(
            patchPaymentType({
              body: buildPaymentTypePatchBody(data.paymentType),
              serviceOrderVariantId,
            })
              .unwrap()
              .catch(handleApiError)
          );
        }

        if (data.deposit) {
          tabsArray.push(
            patchDeposit({
              body: buildDepositPatchBody(data.deposit),
              serviceOrderVariantId,
            })
              .unwrap()
              .catch(handleApiError)
          );
        }

        tabsArray.push(
          patchServiceOrderVariantMandatoryFields({
            serviceOrderVariantId,
            body: mandatoryFields,
          })
            .unwrap()
            .catch(handleApiError)
        );

        tabsArray.push(
          patchServiceOrderVariantAccounting({
            serviceOrderVariantId,
            body: {
              ...data.accounting,
              isTaxDeductible: stringToBoolean(data.accounting.isTaxDeductible),
              fixedVatType: stringToBoolean(data.accounting.isTaxDeductible)
                ? undefined
                : data.accounting.fixedVatType,
            },
          })
            .unwrap()
            .catch(handleApiError)
        );

        tabsArray.push(
          patchServiceOrderVariantReporting({
            serviceOrderVariantId,
            body: {
              ...data.reporting,
              isDefaultServiceOrderGroupLocked: !!data.reporting.isDefaultServiceOrderGroupLocked,
              isPreDeliveryInspection: !!data.reporting.isPreDeliveryInspection,
              isDbiReportBodyshop: !!data.reporting.isDbiReportBodyshop,
            },
          })
            .unwrap()
            .catch(handleApiError)
        );

        tabsArray.push(
          patchServiceOrderVariantIntegration({
            serviceOrderVariantId,
            body: {
              isDefaultForXentryServiceMeasures:
                !!data.integration?.isDefaultForXentryServiceMeasures,
              isExcludedFromXentrySynchronization:
                !!data.integration?.isExcludedFromXentrySynchronization,
              isAutomaticSendToChronos: !!data.integration?.isAutomaticSendToChronos,
            },
          })
            .unwrap()
            .catch(handleApiError)
        );

        if (data.warehousePriorities) {
          tabsArray.push(
            patchServiceOrderVariantWarehousePriority({
              serviceOrderVariantId: response?.serviceOrderVariantId ?? id ?? '',
              body: {
                warehouseIds: data.warehousePriorities,
              },
            })
              .unwrap()
              .catch(handleApiError)
          );
        }

        Promise.all(tabsArray)
          .then(() => navigate(serviceOrderVariantsRoutes.overview))
          .then(() =>
            dispatch(
              documentContextApi.util.invalidateTags([{type: 'documentsCount', id: id ?? ''}])
            )
          );
      })
      .catch(handleApiError);
  };

  const mandatoryFieldsArray =
    mandatoryFields?.map(
      (field) =>
        ({
          [field?.field ?? '']: !!field?.isSelected,
        }) as Record<MandatoryFieldType, boolean>
    ) ?? ([] as Record<MandatoryFieldType, boolean>[]);

  const mandatoryFieldConditionsArray =
    mandatoryFields?.map((field) => {
      const selectedEvent = field?.mandatoryCondition?.find(
        (condition) => condition?.isSelected
      )?.event;

      return {
        [field?.field ?? '']: selectedEvent ? [selectedEvent] : [],
      } as Record<MandatoryFieldType, string[]>;
    }) ?? ([] as Record<MandatoryFieldType, string[]>[]);

  const mandatoryFieldsDefaultValues =
    mergeAll<Record<MandatoryFieldType, boolean>>(mandatoryFieldsArray);

  const mandatoryFieldConditionDefaultValues = mergeAll<Record<MandatoryFieldType, string[]>>(
    mandatoryFieldConditionsArray
  );

  const titleKey = isNil(id)
    ? 'entity.order.actions.newServiceOrderVariant'
    : 'entity.order.labels.serviceOrderVariants';

  const warehousePrioritiesSourceData = isNilOrEmpty(id)
    ? defaultWarehousePriority
    : warehousePriorities;

  const defaultValues: DeepPartial<FormType> = {
    general: {
      ...orderVariant,
      suffix: orderVariant?.suffix ?? undefined,
      priceTypeRatio: decimalValueToPercentage(defaultTo(1, orderVariant?.priceTypeRatio)),
      rateTypeRatio: decimalValueToPercentage(defaultTo(1, orderVariant?.rateTypeRatio)),
      minimalMaterialMargin: decimalValueToPercentage(
        defaultTo(0, orderVariant?.minimalMaterialMargin)
      ),
      minimalWorkMargin: decimalValueToPercentage(defaultTo(0, orderVariant?.minimalWorkMargin)),
      isCancelReasonRequired: orderVariant?.isCancelReasonRequired ?? false,
    },
    documents: buildDocumentsDefaultValues(orderVariantDocuments, series),
    paymentType: buildPaymentTypeDefaultValues(orderVariantPaymentType, {
      resolveStandardInvoiceTemplateId,
      resolveInternalInvoiceTemplateId,
    }),
    deposit: buildDepositDefaultValues(orderVariantDeposit, resolveStandardInvoiceTemplateId),
    mandatoryFields: mandatoryFieldsDefaultValues,
    mandatoryFieldConditions: mandatoryFieldConditionDefaultValues,
    reporting: {
      ...orderVariantReporting,
      excludedServiceOrderGroupIds: orderVariantReporting?.excludedServiceOrderGroupIds ?? [],
      isDefaultServiceOrderGroupLocked:
        orderVariantReporting?.isDefaultServiceOrderGroupLocked ?? false,
      isPreDeliveryInspection: orderVariantReporting?.isPreDeliveryInspection ?? false,
      isDbiReportBodyshop: orderVariantReporting?.isDbiReportBodyshop ?? false,
    },
    accounting: {
      fixedVatType: (orderVariantAccounting?.fixedVatType as VatRateType) ?? DEFAULT_VAT_RATE,
      excludedServiceCenterIds: orderVariantAccounting?.excludedServiceCenterIds ?? [],
      defaultServiceCenterId: orderVariantAccounting?.defaultServiceCenterId ?? undefined,
      isDefaultServiceCenterLocked: orderVariantAccounting?.isDefaultServiceCenterLocked ?? false,
      isTaxDeductible: isBoolean(orderVariantAccounting?.isTaxDeductible)
        ? (orderVariantAccounting.isTaxDeductible as boolean).toString()
        : 'true',
    },
    integration: {
      isDefaultForXentryServiceMeasures:
        orderVariantIntegration?.isDefaultForXentryServiceMeasures ?? false,
      isExcludedFromXentrySynchronization:
        orderVariantIntegration?.isExcludedFromXentrySynchronization ?? false,
      isAutomaticSendToChronos: orderVariantIntegration?.isAutomaticSendToChronos ?? false,
    },
    warehousePriorities:
      warehousePrioritiesSourceData?.map((warehouse) => warehouse.warehouseId) ?? null,
  };

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <DataStatus
        isLoading={
          isOrderVariantLoading ||
          isOrderVariantDocumentsLoading ||
          isMandatoryFieldsLoading ||
          isOrderVariantPaymentTypeLoading ||
          isOrderVariantDepositLoading ||
          isTemplatesLoading ||
          isSeriesLoading ||
          isOrderVariantAccountingFetching ||
          isOrderVariantReportingFetching ||
          isOrderVariantIntegrationFetching ||
          isDefaultWarehousePriorityFetching ||
          isWarehousePrioritiesLoading
        }
        isError={
          isOrderVariantError ||
          isOrderVariantDocumentsError ||
          isMandatoryFieldsError ||
          isOrderVariantPaymentTypeError ||
          isOrderVariantDepositError ||
          isOrderVariantAccountingError ||
          isOrderVariantReportingError ||
          isOrderVariantIntegrationError ||
          isDefaultWarehousePriorityError ||
          isWarehousePrioritiesError ||
          isSeriesError
        }
        minHeight={60}
      >
        <Form<FormType>
          mode="onChange"
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          experimentalZodSchema={ServiceOrderVariantsDetailFormSchema}
          onInvalidSubmit={(errors) => {
            const invalidTab = TABS_BY_FORM_SECTION.find(([section]) =>
              isNotNil(errors[section])
            )?.[1];

            if (isNotNil(invalidTab)) {
              updateQuery(invalidTab);
            }
          }}
          isFullHeight
          isDisabled={isSalesRoute}
        >
          {(control, formApi) => {
            const selectedOrderTypeId = formApi.watch('general.serviceOrderTypeId');
            const selectedOrderType = orderTypes?.find((type) => type?.id === selectedOrderTypeId);

            return (
              <SettingsTemplate
                header={{
                  title: `${
                    orderVariant?.name ?? i18n.t('entity.order.actions.newServiceOrderVariant')
                  }${selectedOrderType ? ` (${selectedOrderType?.name})` : ''}`,
                  breadcrumbs: [
                    {
                      label: i18n.t('entity.order.labels.serviceOrderVariants'),
                      href: serviceOrderVariantsRoutes.overview,
                    },
                  ],
                }}
                tabs={[
                  {
                    title: i18n.t('general.labels.general'),
                    queryId: GENERAL_TAB,
                    content: <GeneralTab control={control} formApi={formApi} />,
                  },
                  {
                    title: i18n.t('general.labels.documents'),
                    queryId: DOCUMENTS_TAB,
                    content: (
                      <DocumentsTab
                        control={control}
                        formApi={formApi}
                        serviceOrderTypeCode={selectedOrderType?.code}
                        serviceOrderVariantId={id}
                      />
                    ),
                  },
                  {
                    title: i18n.t('entity.bank.labels.paymentType'),
                    queryId: PAYMENTS_TAB,
                    content: <PaymentTab control={control} formApi={formApi} />,
                  },
                  {
                    title: i18n.t('entity.order.labels.deposit'),
                    queryId: DEPOSIT_TAB,
                    content: (
                      <DepositTab
                        control={control}
                        formApi={formApi}
                        invoiceTemplateOptions={standardInvoice.options}
                        isTemplatesLoading={isTemplatesLoading}
                      />
                    ),
                  },
                  {
                    title: i18n.t('entity.accounting.labels.title'),
                    queryId: ACCOUNTING_TAB,
                    content: (
                      <AccountingTab
                        control={control}
                        formApi={formApi}
                        data-testid={testIds.settings.serviceOrderVariantsDetail('accounting')}
                      />
                    ),
                  },
                  {
                    title: i18n.t('entity.order.labels.reporting'),
                    queryId: REPORTING_TAB,
                    content: (
                      <ReportingTab
                        control={control}
                        formApi={formApi}
                        data-testid={testIds.settings.serviceOrderVariantsDetail('reporting')}
                      />
                    ),
                  },
                  {
                    title: i18n.t('entity.order.labels.mandatoryFields'),
                    queryId: MANDATORY_FIELDS_TAB,
                    content: (
                      <MandatoryFieldsTab
                        control={control}
                        formApi={formApi}
                        selectedOrderTypeCode={selectedOrderType?.code}
                      />
                    ),
                    isDisabled: isNilOrEmpty(formApi.watch('general.serviceOrderTypeId')),
                  },
                  {
                    title: i18n.t('entity.warehouse.labels.warehouses'),
                    queryId: WAREHOUSES_TAB,
                    content: (
                      <WarehousePrioritiesTab
                        control={control}
                        formApi={formApi}
                        sourceData={warehousePrioritiesSourceData}
                      />
                    ),
                  },
                  {
                    title: i18n.t('entity.serviceOrder.labels.integration'),
                    queryId: INTEGRATION_TAB,
                    content: <IntegrationTab control={control} formApi={formApi} />,
                    isDisabled: isNilOrEmpty(id),
                  },
                ]}
                isCreating={isNil(id)}
                data-testid={testIds.settings.serviceOrderVariantsDetail('template')}
              />
            );
          }}
        </Form>
      </DataStatus>
    </>
  );
}
